import {
    useEffect,
    useMemo,
    useState,
    type ChangeEvent,
    type Dispatch,
    type FormEvent,
    type SetStateAction
} from "react";

import {
    AdminContentRevisionHistory
} from "./AdminContentRevisionHistory.js";

import {
    AdminGrammarEditorFields
} from "./AdminGrammarEditorFields.js";

import {
    AdminHomeFeatureEditorFields
} from "./AdminHomeFeatureEditorFields.js";

import {
    AdminNewsEditorFields
} from "./AdminNewsEditorFields.js";

import {
    AdminVocabularyEditorFields
} from "./AdminVocabularyEditorFields.js";

import {
    AdminTravelEditorFields
} from "./AdminTravelEditorFields.js";

import {
    readAdminTravelEditor,
    updateAdminTravelEditor,
    validateAdminTravelEditor
} from "./adminTravelEditor.js";

import {
    AdminEditorField
} from "./AdminEditorField.js";

import {
    readAdminGrammarEditor,
    updateAdminGrammarEditor
} from "./adminGrammarEditor.js";

import {
    validateAdminGrammarEditor
} from "./adminGrammarValidation.js";

import {
    readAdminVocabularyEditor,
    updateAdminVocabularyEditor
} from "./adminVocabularyEditor.js";

import {
    validateAdminVocabularyEditor
} from "./adminVocabularyValidation.js";

import {
    normalizeWord
} from "./AdminVocabularyWords.js";

import {
    useAdminText
} from "./adminI18n.js";

import {
    readAdminNewsEditor,
    updateAdminNewsEditor,
    validateAdminNewsEditor
} from "./adminNewsEditor.js";

import {
    readAdminHomeFeatureEditor,
    updateAdminHomeFeatureEditor,
    validateAdminHomeFeatureEditor
} from "./adminHomeFeatureEditor.js";

import {
    createAdminContentEditorFromRevision,
    createAdminPayloadTemplate,
    createEmptyAdminContentEditor,
    parseAdminContentEditor
} from "./adminContentEditor.js";

import type {
    AdminContentEditorValue
} from "./adminContentEditor.js";

import {
    loadAdminContentRevisions,
    publishAdminContentRevision,
    saveAdminContentDraft
} from "./adminContentRepository.js";

import type {
    AdminContentItemRpcRow,
    AdminContentRevisionRpcRow,
    CanonicalContentType
} from "../../services/backend/database.types.js";

import type {
    DinoBackendClient
} from "../../services/backend/supabaseClient.js";

import {
    Button,
    Input,
    Select,
    Textarea
} from "../../ui/components/Controls.js";

import {
    Alert
} from "../../ui/components/Feedback.js";

import {
    Section,
    SectionHeader
} from "../../ui/components/Layout.js";


/* -------------------------------------------------------------------------- */
/* Props                                                                      */
/* -------------------------------------------------------------------------- */

interface AdminContentEditorProps {
    availableItems:
    readonly AdminContentItemRpcRow[];

    client:
    DinoBackendClient;

    item:
    AdminContentItemRpcRow
    | null;

    onChanged:
    (
        identity?: {
            contentKey: string;
            contentType: CanonicalContentType;
        }
    ) => void;
}


/* -------------------------------------------------------------------------- */
/* Content types                                                              */
/* -------------------------------------------------------------------------- */

const editorContentTypes: Array<{
    label: string;
    value: CanonicalContentType;
}> = [
        {
            label:
                "Grammaire",

            value:
                "grammar_lesson"
        },
        {
            label:
                "Actualité",

            value:
                "news_article"
        },
        {
            label:
                "Bloc d’accueil",

            value:
                "home_feature"
        },
        {
            label:
                "Voyage",

            value:
                "travel_lesson"
        },
        {
            label:
                "Vocabulaire",

            value:
                "vocabulary_pack"
        }
    ];


/* -------------------------------------------------------------------------- */
/* Editor                                                                     */
/* -------------------------------------------------------------------------- */

function AdminContentEditor({
    availableItems,
    client,
    item,
    onChanged
}: AdminContentEditorProps) {
    const adminText =
        useAdminText();

    const [
        value,
        setValue
    ] =
        useState<AdminContentEditorValue>(
            createEmptyAdminContentEditor
        );

    const [
        revisions,
        setRevisions
    ] =
        useState<
            AdminContentRevisionRpcRow[]
        >(
            []
        );

    const [
        selectedRevision,
        setSelectedRevision
    ] =
        useState<
            number
            | null
        >(
            null
        );

    const [
        revisionRefresh,
        setRevisionRefresh
    ] =
        useState(
            0
        );

    const [
        loading,
        setLoading
    ] =
        useState(
            false
        );

    const [
        action,
        setAction
    ] =
        useState<
            "idle"
            | "saving"
            | "publishing"
        >(
            "idle"
        );

    const [
        message,
        setMessage
    ] =
        useState<
            string
            | null
        >(
            null
        );

    const [
        error,
        setError
    ] =
        useState<
            string
            | null
        >(
            null
        );

    const [
        dirty,
        setDirty
    ] =
        useState(
            false
        );


    /* ---------------------------------------------------------------------- */
    /* Draft update                                                           */
    /* ---------------------------------------------------------------------- */

    const setDraftValue: Dispatch<
        SetStateAction<AdminContentEditorValue>
    > =
        nextValue => {
            setDirty(
                true
            );

            setValue(
                nextValue
            );
        };


    /* ---------------------------------------------------------------------- */
    /* News validation                                                        */
    /* ---------------------------------------------------------------------- */

    const newsIssues =
        value.contentType
            === "news_article"
            ? validateAdminNewsEditor(
                readAdminNewsEditor(
                    value
                ),
                availableItems
            )
            : [];

    const newsErrors =
        newsIssues.filter(
            issue =>
                issue.severity
                === "error"
        );


    /* ---------------------------------------------------------------------- */
    /* Home feature validation                                                */
    /* ---------------------------------------------------------------------- */

    const homeFeatureIssues =
        value.contentType
            === "home_feature"
            ? validateAdminHomeFeatureEditor(
                readAdminHomeFeatureEditor(
                    value
                )
            )
            : [];

    const homeFeatureErrors =
        homeFeatureIssues.filter(
            issue =>
                issue.severity
                === "error"
        );


    /* ---------------------------------------------------------------------- */
    /* Grammar validation                                                     */
    /* ---------------------------------------------------------------------- */

    const grammarIssues =
        value.contentType
            === "grammar_lesson"
            ? validateAdminGrammarEditor(
                readAdminGrammarEditor(
                    value
                ),
                availableItems,
                item
                    ?.published_revision_number
                == null
            )
            : [];

    const grammarErrors =
        grammarIssues.filter(
            issue =>
                issue.severity
                === "error"
        );


    /* ---------------------------------------------------------------------- */
    /* Vocabulary validation                                                  */
    /* ---------------------------------------------------------------------- */

    const vocabularyIssues =
        value.contentType
            === "vocabulary_pack"
            ? validateAdminVocabularyEditor(
                readAdminVocabularyEditor(
                    value
                )
            )
            : [];

    const vocabularyErrors =
        vocabularyIssues.filter(
            issue =>
                issue.severity
                === "error"
        );


    /* ---------------------------------------------------------------------- */
    /* Travel validation                                                      */
    /* ---------------------------------------------------------------------- */

    const travelErrors =
        value.contentType
            === "travel_lesson"
            ? validateAdminTravelEditor(
                readAdminTravelEditor(
                    value
                ),
                value.contentKey
            )
            : [];


    /* ---------------------------------------------------------------------- */
    /* Publication validation                                                 */
    /* ---------------------------------------------------------------------- */

    const publicationErrors = [
        ...newsErrors,
        ...homeFeatureErrors,
        ...grammarErrors,
        ...vocabularyErrors,
        ...travelErrors
    ];


    /* ---------------------------------------------------------------------- */
    /* Immutable published content                                            */
    /* ---------------------------------------------------------------------- */

    const lockedGrammarSectionIds =
        useMemo(
            () =>
                getPublishedGrammarSectionIds(
                    item,
                    revisions
                ),
            [
                item,
                revisions
            ]
        );

    const lockedVocabularyWords =
        useMemo(
            () =>
                getPublishedVocabularyWords(
                    item,
                    revisions
                ),
            [
                item,
                revisions
            ]
        );


    /* ---------------------------------------------------------------------- */
    /* Publication state                                                      */
    /* ---------------------------------------------------------------------- */

    const publicationBlockedReason =
        dirty
            ? adminText(
                "Enregistrez vos modifications en brouillon avant de publier."
            )
            : publicationErrors.length
                > 0
                ? `${publicationErrors.length} ${adminText(
                    "erreur(s) éditoriale(s) bloquent la publication."
                )}`
                : undefined;


    /* ---------------------------------------------------------------------- */
    /* Reset feedback when selection changes                                  */
    /* ---------------------------------------------------------------------- */

    useEffect(
        () => {
            setMessage(
                null
            );

            setError(
                null
            );
        },
        [
            item?.content_key,
            item?.content_type
        ]
    );


    /* ---------------------------------------------------------------------- */
    /* Load revisions                                                         */
    /* ---------------------------------------------------------------------- */

    useEffect(
        () => {
            if (
                !item
            ) {
                setValue(
                    createEmptyAdminContentEditor()
                );

                setDirty(
                    false
                );

                setRevisions(
                    []
                );

                setSelectedRevision(
                    null
                );

                return;
            }

            let active =
                true;

            setLoading(
                true
            );

            void loadAdminContentRevisions(
                client,
                item.content_type,
                item.content_key
            ).then(
                loaded => {
                    if (
                        !active
                    ) {
                        return;
                    }

                    setRevisions(
                        loaded
                    );

                    setLoading(
                        false
                    );

                    if (
                        loaded.length
                        > 0
                    ) {
                        setSelectedRevision(
                            loaded[0]
                                .revision_number
                        );

                        setValue(
                            createAdminContentEditorFromRevision(
                                item,
                                loaded[0]
                            )
                        );

                        setDirty(
                            false
                        );
                    }
                },
                reason => {
                    if (
                        active
                    ) {
                        setLoading(
                            false
                        );

                        setError(
                            getErrorMessage(
                                reason
                            )
                        );
                    }
                }
            );

            return () => {
                active =
                    false;
            };
        },
        [
            client,
            item,
            revisionRefresh
        ]
    );


    /* ---------------------------------------------------------------------- */
    /* Save                                                                   */
    /* ---------------------------------------------------------------------- */

    async function saveDraft(
        event:
            FormEvent
    ): Promise<void> {
        event.preventDefault();

        setAction(
            "saving"
        );

        setMessage(
            null
        );

        setError(
            null
        );

        try {
            const result =
                await saveAdminContentDraft(
                    client,
                    parseAdminContentEditor(
                        value
                    )
                );

            setMessage(
                `${adminText("Révision")} ${result.revision_number} ${adminText(
                    "enregistrée en brouillon."
                )}`
            );

            onChanged({
                contentKey:
                    result.content_key,

                contentType:
                    result.content_type
            });

            if (
                item
            ) {
                setRevisionRefresh(
                    current =>
                        current + 1
                );
            }
        } catch (reason) {
            setError(
                getErrorMessage(
                    reason
                )
            );
        } finally {
            setAction(
                "idle"
            );
        }
    }


    /* ---------------------------------------------------------------------- */
    /* Publish                                                                */
    /* ---------------------------------------------------------------------- */

    async function publishRevision():
        Promise<void> {
        if (
            !item
            || !selectedRevision
        ) {
            return;
        }

        setAction(
            "publishing"
        );

        setMessage(
            null
        );

        setError(
            null
        );

        try {
            await publishAdminContentRevision(
                client,
                item.content_type,
                item.content_key,
                selectedRevision
            );

            setMessage(
                `${adminText("Révision")} ${selectedRevision} ${adminText(
                    "publiée."
                )}`
            );

            onChanged({
                contentKey:
                    item.content_key,

                contentType:
                    item.content_type
            });

            setRevisionRefresh(
                current =>
                    current + 1
            );
        } catch (reason) {
            setError(
                getErrorMessage(
                    reason
                )
            );
        } finally {
            setAction(
                "idle"
            );
        }
    }


    /* ---------------------------------------------------------------------- */
    /* Render                                                                 */
    /* ---------------------------------------------------------------------- */

    return (
        <Section
            className="min-w-0"
        >
            <SectionHeader
                title={
                    item
                        ? `${adminText("Modifier")} ${item.content_key}`
                        : adminText(
                            "Nouveau contenu"
                        )
                }
                description={
                    adminText(
                        "Chaque sauvegarde produit une révision immuable."
                    )
                }
            />

            {error ? (
                <Alert
                    className="mb-3"
                    variant="danger"
                    title={
                        adminText(
                            "Édition interrompue"
                        )
                    }
                >
                    {error}
                </Alert>
            ) : null}

            {message ? (
                <Alert
                    className="mb-3"
                    variant="success"
                    title={
                        adminText(
                            "Terminé"
                        )
                    }
                >
                    {message}
                </Alert>
            ) : null}

            <form
                className="
                    space-y-4
                    rounded-card
                    border
                    border-line
                    bg-surface
                    p-4
                "
                onSubmit={
                    event =>
                        void saveDraft(
                            event
                        )
                }
            >
                {/* ---------------------------------------------------------- */}
                {/* Identity                                                   */}
                {/* ---------------------------------------------------------- */}

                <div
                    className="
                        grid
                        gap-3
                        sm:grid-cols-2
                    "
                >
                    <AdminEditorField
                        label="Type"
                    >
                        <Select
                            aria-label={
                                adminText(
                                    "Type de contenu"
                                )
                            }
                            disabled={
                                Boolean(
                                    item
                                )
                            }
                            value={
                                value.contentType
                            }
                            onChange={
                                event => {
                                    updateEditorField(
                                        setDraftValue,
                                        "contentType",
                                        event.target.value as CanonicalContentType
                                    );
                                }
                            }
                        >
                            {editorContentTypes.map(
                                option => (
                                    <option
                                        key={
                                            option.value
                                        }
                                        value={
                                            option.value
                                        }
                                    >
                                        {adminText(
                                            option.label
                                        )}
                                    </option>
                                )
                            )}
                        </Select>
                    </AdminEditorField>

                    <AdminEditorField
                        label="Identifiant stable"
                    >
                        <Input
                            className="font-mono"
                            disabled={
                                Boolean(
                                    item
                                )
                            }
                            required
                            value={
                                value.contentKey
                            }
                            onChange={
                                changeEditor(
                                    "contentKey",
                                    setDraftValue
                                )
                            }
                        />
                    </AdminEditorField>
                </div>


                {/* ---------------------------------------------------------- */}
                {/* Domain editor                                              */}
                {/* ---------------------------------------------------------- */}

                {value.contentType
                    === "home_feature" ? (
                    <AdminHomeFeatureEditorFields
                        value={
                            value
                        }
                        onChange={
                            setDraftValue
                        }
                    />
                ) : value.contentType
                    === "news_article" ? (
                    <AdminNewsEditorFields
                        availableItems={
                            availableItems
                        }
                        value={
                            value
                        }
                        onChange={
                            setDraftValue
                        }
                    />
                ) : value.contentType
                    === "grammar_lesson" ? (
                    <AdminGrammarEditorFields
                        availableItems={
                            availableItems
                        }
                        lockedSectionIds={
                            lockedGrammarSectionIds
                        }
                        requireCompletePractice={
                            item
                                ?.published_revision_number
                            == null
                        }
                        value={
                            value
                        }
                        onChange={
                            setDraftValue
                        }
                    />
                ) : value.contentType
                    === "vocabulary_pack" ? (
                    <AdminVocabularyEditorFields
                        lockedWords={
                            lockedVocabularyWords
                        }
                        value={
                            value
                        }
                        onChange={
                            setDraftValue
                        }
                    />
                ) : value.contentType
                    === "travel_lesson" ? (
                    <AdminTravelEditorFields
                        value={
                            value
                        }
                        onChange={
                            setDraftValue
                        }
                    />
                ) : (
                    <GenericContentFields
                        value={
                            value
                        }
                        setValue={
                            setDraftValue
                        }
                    />
                )}


                {/* ---------------------------------------------------------- */}
                {/* Technical configuration                                    */}
                {/* ---------------------------------------------------------- */}

                <details>
                    <summary
                        className="
                            cursor-pointer
                            text-sm
                            font-bold
                            text-dino-700
                        "
                    >
                        {adminText(
                            "Paramètres techniques"
                        )}
                    </summary>

                    <div
                        className="
                            mt-3
                            grid
                            gap-3
                            sm:grid-cols-2
                        "
                    >
                        <AdminEditorField
                            label="Version du schéma"
                        >
                            <Input
                                min="1"
                                required
                                type="number"
                                value={
                                    value.schemaVersion
                                }
                                onChange={
                                    changeEditor(
                                        "schemaVersion",
                                        setDraftValue
                                    )
                                }
                            />
                        </AdminEditorField>

                        <AdminEditorField
                            label="Provenance"
                        >
                            <Input
                                className="font-mono"
                                value={
                                    value.sourcePath
                                }
                                onChange={
                                    changeEditor(
                                        "sourcePath",
                                        setDraftValue
                                    )
                                }
                            />
                        </AdminEditorField>
                    </div>
                </details>


                {/* ---------------------------------------------------------- */}
                {/* Save                                                       */}
                {/* ---------------------------------------------------------- */}

                <Button
                    fullWidth
                    type="submit"
                    disabled={
                        action !== "idle"
                    }
                >
                    {action
                        === "saving"
                        ? adminText(
                            "Enregistrement…"
                        )
                        : adminText(
                            "Enregistrer en brouillon"
                        )}
                </Button>
            </form>


            {/* -------------------------------------------------------------- */}
            {/* History                                                        */}
            {/* -------------------------------------------------------------- */}

            {item ? (
                <AdminContentRevisionHistory
                    action={
                        action
                    }
                    loading={
                        loading
                    }
                    publicationBlockedReason={
                        publicationBlockedReason
                    }
                    revisions={
                        revisions
                    }
                    selectedRevision={
                        selectedRevision
                    }
                    onPublish={
                        () =>
                            void publishRevision()
                    }
                    onSelect={
                        revision => {
                            setSelectedRevision(
                                revision
                                    .revision_number
                            );

                            setValue(
                                createAdminContentEditorFromRevision(
                                    item,
                                    revision
                                )
                            );

                            setDirty(
                                false
                            );
                        }
                    }
                />
            ) : null}
        </Section>
    );
}


/* -------------------------------------------------------------------------- */
/* Generic editor                                                             */
/* -------------------------------------------------------------------------- */

function GenericContentFields({
    setValue,
    value
}: {
    setValue:
    Dispatch<
        SetStateAction<AdminContentEditorValue>
    >;

    value:
    AdminContentEditorValue;
}) {
    return (
        <>
            <AdminEditorField
                label="Titre français"
            >
                <Input
                    required
                    value={
                        value.titleFr
                    }
                    onChange={
                        changeEditor(
                            "titleFr",
                            setValue
                        )
                    }
                />
            </AdminEditorField>

            <div
                className="
                    grid
                    gap-3
                    sm:grid-cols-2
                "
            >
                <AdminEditorField
                    label="Niveau"
                >
                    <Input
                        value={
                            value.level
                        }
                        onChange={
                            changeEditor(
                                "level",
                                setValue
                            )
                        }
                    />
                </AdminEditorField>

                <AdminEditorField
                    label="Titre persan"
                >
                    <Input
                        dir="rtl"
                        value={
                            value.titleFa
                        }
                        onChange={
                            changeEditor(
                                "titleFa",
                                setValue
                            )
                        }
                    />
                </AdminEditorField>
            </div>

            <div>
                <div
                    className="
                        mb-2
                        flex
                        flex-wrap
                        items-center
                        justify-between
                        gap-2
                    "
                >
                    <label
                        className="
                            text-sm
                            font-bold
                            text-ink
                        "
                        htmlFor="admin-payload"
                    >
                        Payload JSON
                    </label>

                    <Button
                        variant="ghost"
                        onClick={
                            () =>
                                setValue(
                                    current => ({
                                        ...current,

                                        payloadText:
                                            createAdminPayloadTemplate(
                                                current
                                            )
                                    })
                                )
                        }
                    >
                        Générer un modèle
                    </Button>
                </div>

                <Textarea
                    id="admin-payload"
                    className="
                        min-h-80
                        resize-y
                        font-mono
                        text-xs
                        leading-5
                    "
                    required
                    spellCheck={
                        false
                    }
                    value={
                        value.payloadText
                    }
                    onChange={
                        changeEditor(
                            "payloadText",
                            setValue
                        )
                    }
                />
            </div>
        </>
    );
}


/* -------------------------------------------------------------------------- */
/* Input helpers                                                              */
/* -------------------------------------------------------------------------- */

function changeEditor(
    field:
        keyof AdminContentEditorValue,

    setValue:
        Dispatch<
            SetStateAction<AdminContentEditorValue>
        >
) {
    return (
        event:
            ChangeEvent<
                HTMLInputElement
                | HTMLTextAreaElement
            >
    ): void => {
        updateEditorField(
            setValue,
            field,
            event.target.value
        );
    };
}


/* -------------------------------------------------------------------------- */
/* Editor state synchronization                                               */
/* -------------------------------------------------------------------------- */

function updateEditorField(
    setValue:
        Dispatch<
            SetStateAction<AdminContentEditorValue>
        >,

    field:
        keyof AdminContentEditorValue,

    value:
        string
): void {
    setValue(
        current => {
            let updated = {
                ...current,

                [field]:
                    value
            };


            /* ------------------------------------------------------------------ */
            /* Type changed                                                       */
            /* ------------------------------------------------------------------ */

            if (
                field
                === "contentType"
            ) {
                /*
                 * home_feature is intentionally not tied to a CEFR level.
                 * Its schema starts at v1.
                 */
                if (
                    value
                    === "home_feature"
                ) {
                    updated = {
                        ...updated,

                        level:
                            "",

                        schemaVersion:
                            "1"
                    };
                }

                updated = {
                    ...updated,

                    payloadText:
                        createAdminPayloadTemplate(
                            updated
                        )
                };
            }


            /* ------------------------------------------------------------------ */
            /* Home feature                                                       */
            /* ------------------------------------------------------------------ */

            if (
                updated.contentType
                === "home_feature"
                && (
                    field
                    === "contentType"
                    || field
                    === "contentKey"
                )
            ) {
                const feature =
                    readAdminHomeFeatureEditor(
                        updated
                    );

                return updateAdminHomeFeatureEditor(
                    updated,
                    {
                        ...feature,

                        contentKey:
                            updated.contentKey
                    }
                );
            }


            /* ------------------------------------------------------------------ */
            /* News                                                               */
            /* ------------------------------------------------------------------ */

            if (
                updated.contentType
                === "news_article"
                && (
                    field
                    === "contentType"
                    || field
                    === "contentKey"
                )
            ) {
                const news =
                    readAdminNewsEditor(
                        updated
                    );

                return updateAdminNewsEditor(
                    updated,
                    {
                        ...news,

                        contentKey:
                            updated.contentKey
                    }
                );
            }


            /* ------------------------------------------------------------------ */
            /* Grammar                                                            */
            /* ------------------------------------------------------------------ */

            if (
                updated.contentType
                === "grammar_lesson"
                && (
                    field
                    === "contentType"
                    || field
                    === "contentKey"
                )
            ) {
                const grammar =
                    readAdminGrammarEditor(
                        updated
                    );

                return updateAdminGrammarEditor(
                    updated,
                    {
                        ...grammar,

                        contentKey:
                            updated.contentKey
                    }
                );
            }


            /* ------------------------------------------------------------------ */
            /* Vocabulary                                                         */
            /* ------------------------------------------------------------------ */

            if (
                updated.contentType
                === "vocabulary_pack"
                && (
                    field
                    === "contentType"
                    || field
                    === "contentKey"
                )
            ) {
                const vocabulary =
                    readAdminVocabularyEditor(
                        updated
                    );

                return updateAdminVocabularyEditor(
                    updated,
                    {
                        ...vocabulary,

                        contentKey:
                            updated.contentKey
                    }
                );
            }


            /* ------------------------------------------------------------------ */
            /* Travel                                                             */
            /* ------------------------------------------------------------------ */

            if (
                updated.contentType
                === "travel_lesson"
                && (
                    field
                    === "contentType"
                    || field
                    === "contentKey"
                )
            ) {
                const travel =
                    readAdminTravelEditor(
                        updated
                    );

                return updateAdminTravelEditor(
                    updated,
                    travel
                );
            }

            return updated;
        }
    );
}


/* -------------------------------------------------------------------------- */
/* Errors                                                                     */
/* -------------------------------------------------------------------------- */

function getErrorMessage(
    reason:
        unknown
): string {
    return (
        reason
            instanceof Error
            ? reason.message
            : "Une erreur inattendue est survenue."
    );
}


/* -------------------------------------------------------------------------- */
/* Published Grammar locks                                                    */
/* -------------------------------------------------------------------------- */

function getPublishedGrammarSectionIds(
    item:
        AdminContentItemRpcRow
        | null,

    revisions:
        readonly AdminContentRevisionRpcRow[]
): ReadonlySet<string> {
    if (
        item?.content_type
        !== "grammar_lesson"
        || item.published_revision_number
        === null
    ) {
        return new Set();
    }

    const revision =
        revisions.find(
            candidate =>
                candidate.revision_number
                === item.published_revision_number
        );

    if (
        !revision
    ) {
        return new Set();
    }

    const grammar =
        readAdminGrammarEditor(
            createAdminContentEditorFromRevision(
                item,
                revision
            )
        );

    return new Set(
        [
            ...grammar.lessons.map(
                section =>
                    section.id
            ),

            ...grammar.exercises.map(
                section =>
                    section.id
            )
        ].filter(
            Boolean
        )
    );
}


/* -------------------------------------------------------------------------- */
/* Published Vocabulary locks                                                 */
/* -------------------------------------------------------------------------- */

function getPublishedVocabularyWords(
    item:
        AdminContentItemRpcRow
        | null,

    revisions:
        readonly AdminContentRevisionRpcRow[]
): ReadonlySet<string> {
    if (
        item?.content_type
        !== "vocabulary_pack"
        || item.published_revision_number
        === null
    ) {
        return new Set();
    }

    const revision =
        revisions.find(
            candidate =>
                candidate.revision_number
                === item.published_revision_number
        );

    if (
        !revision
    ) {
        return new Set();
    }

    const vocabulary =
        readAdminVocabularyEditor(
            createAdminContentEditorFromRevision(
                item,
                revision
            )
        );

    return new Set(
        vocabulary.words
            .map(
                word =>
                    normalizeWord(
                        word.fr
                    )
            )
            .filter(
                Boolean
            )
    );
}


/* -------------------------------------------------------------------------- */
/* Export                                                                     */
/* -------------------------------------------------------------------------- */

export {
    AdminContentEditor
};